/* Admin: Donations log — pledged/received, optionally event-scoped. */
import { useEffect, useState } from "react";
import { Link, useRoute } from "../../lib/router-compat.jsx";
import AdminShell from "../../components/shell/AdminShell.jsx";
import { Button, Field, Input, Textarea, Select, Card, Badge, Modal, EmptyState, Skeleton, ConfirmDialog, Icon, showToast } from "../../ui/index.js";
import { useDonations, useLeads, useEvents } from "../../data/hooks.js";
import { saveDonation, markDonationReceived } from "../../data/mutations.js";
import { deleteDoc, doc } from "firebase/firestore";
import { db } from "../../lib/firebase.js";
import { COL } from "../../data/collections.js";
import { CURRENCY_FMT } from "../../utils/format.js";

export default function AdminDonationsPage() {
  const route = useRoute();
  const { donations, loading } = useDonations();
  const { leads } = useLeads();
  const { events } = useEvents();
  const [filter, setFilter] = useState("all");
  const [editing, setEditing] = useState(null);
  const [deleting, setDeleting] = useState(null);

  useEffect(() => {
    if (route.query.prefill) {
      const lead = leads.find((l) => l.id === route.query.prefill);
      if (lead) setEditing({
        donorName: lead.donorName, donorEmail: lead.donorEmail, amount: lead.intendedAmount, currency: lead.currency,
        method: lead.method === "either" ? "bank" : lead.method, status: "pledged", linkedLeadId: lead.id,
        eventId: lead.eventId || "", notes: "", _prefillFromLead: lead.donorName,
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [route.query.prefill, leads.length]);

  const filtered = donations.filter((d) => (filter === "all" ? true : d.status === filter));
  const totals = donations.filter((d) => d.status === "received").reduce((acc, d) => { acc[d.currency] = (acc[d.currency] || 0) + Number(d.amount); return acc; }, {});

  const onDelete = async () => {
    try { await deleteDoc(doc(db, COL.donations, deleting.id)); showToast({ intent: "success", title: "Donation removed" }); }
    catch (err) { showToast({ intent: "danger", title: "Couldn't delete", body: err.message }); }
    setDeleting(null);
  };

  return (
    <AdminShell>
      <div className="admin-main__header">
        <div><h1 className="t-3xl">Donations</h1><p>Log and track every donation, pledged or received.</p></div>
        <Button variant="primary" onClick={() => setEditing({ donorName: "", donorEmail: "", amount: "", currency: "GHS", method: "bank", status: "pledged", receivedDate: "", eventId: "", notes: "" })}><Icon.Plus size={16} />Log new donation</Button>
      </div>

      <div style={{ display: "grid", gridTemplateColumns: `repeat(${Math.max(1, Object.keys(totals).length)}, 1fr)`, gap: 16, marginBottom: 32 }}>
        {Object.keys(totals).length === 0 ? (
          <Card style={{ padding: 20 }}><div className="t-xs muted" style={{ textTransform: "uppercase", letterSpacing: "0.05em" }}>Total received</div><div className="t-3xl" style={{ marginTop: 4, color: "var(--primary)" }}>—</div></Card>
        ) : (
          Object.entries(totals).map(([cur, amt]) => (
            <Card key={cur} style={{ padding: 20 }}>
              <div className="t-xs muted" style={{ textTransform: "uppercase", letterSpacing: "0.05em" }}>Total received in {cur}</div>
              <div className="stat-card__value" style={{ fontSize: 36, marginTop: 8 }}>{cur} {amt.toLocaleString()}</div>
              <div className="t-xs muted" style={{ marginTop: 6 }}>{donations.filter((d) => d.status === "received" && d.currency === cur).length} donations</div>
            </Card>
          ))
        )}
      </div>

      <div className="chips" style={{ marginBottom: 24 }}>
        {[["all", "All"], ["pledged", "Pledged"], ["received", "Received"]].map(([k, label]) => (
          <button key={k} className={`chip ${filter === k ? "active" : ""}`} onClick={() => setFilter(k)}>{label} ({k === "all" ? donations.length : donations.filter((d) => d.status === k).length})</button>
        ))}
      </div>

      {loading ? <Skeleton h={240} /> : filtered.length === 0 ? (
        <Card><EmptyState icon={<Icon.Gift size={40} />} title="No donations logged yet." body="Log a donation manually when one arrives." action={<Button variant="primary" onClick={() => setEditing({ donorName: "", amount: "", currency: "GHS", method: "bank", status: "pledged", eventId: "", notes: "" })}>Log first donation</Button>} /></Card>
      ) : (
        <table className="table">
          <thead><tr><th>Donor</th><th style={{ width: 150 }}>Amount</th><th style={{ width: 130 }}>Method</th><th style={{ width: 120 }}>Status</th><th style={{ width: 140 }}>Event</th><th style={{ width: 140 }}>Date</th><th className="table__actions">Actions</th></tr></thead>
          <tbody>
            {filtered.map((d) => (
              <tr key={d.id} className="clickable" onClick={() => setEditing(d)}>
                <td>
                  <div style={{ fontWeight: 600 }}>{d.donorName || "Anonymous"}</div>
                  {d.donorEmail && <div className="t-xs muted" style={{ marginTop: 2 }}>{d.donorEmail}</div>}
                </td>
                <td style={{ fontWeight: 600 }}>{CURRENCY_FMT(d.amount, d.currency)}</td>
                <td className="t-sm" style={{ textTransform: "capitalize" }}>{d.method === "momo" ? "Mobile money" : d.method === "bank" ? "Bank transfer" : d.method}</td>
                <td><Badge variant={d.status === "received" ? "received" : "pledged"}>{d.status[0].toUpperCase() + d.status.slice(1)}</Badge></td>
                <td className="t-sm">{d.eventId || <span className="muted">General</span>}</td>
                <td className="t-sm">{d.status === "received" ? d.receivedDate : <span className="muted">Pledged</span>}</td>
                <td className="table__actions" onClick={(e) => e.stopPropagation()}>
                  <Button variant="ghost" size="sm" onClick={() => setEditing(d)} aria-label="Edit"><Icon.Pencil size={14} /></Button>
                  <Button variant="ghost" size="sm" onClick={() => setDeleting(d)} aria-label="Delete"><Icon.Trash size={14} /></Button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      )}

      {editing && <DonationFormModal donation={editing} events={events} onClose={() => setEditing(null)} />}
      <ConfirmDialog open={!!deleting} title="Delete this donation?" body="This cannot be undone." confirmLabel="Delete" destructive onConfirm={onDelete} onClose={() => setDeleting(null)} />
    </AdminShell>
  );
}

function DonationFormModal({ donation, events, onClose }) {
  const [form, setForm] = useState({
    donorName: donation.donorName || "", donorEmail: donation.donorEmail || "", amount: donation.amount || "",
    currency: donation.currency || "GHS", method: donation.method || "bank", status: donation.status || "pledged",
    receivedDate: donation.receivedDate || "", eventId: donation.eventId || "", notes: donation.notes || "",
    linkedLeadId: donation.linkedLeadId || null,
  });
  const [errors, setErrors] = useState({});
  const [saving, setSaving] = useState(false);
  const update = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));
  const wasReceived = donation.status === "received";

  const submit = async () => {
    const errs = {};
    if (!form.donorName.trim()) errs.donorName = "Required.";
    if (!form.amount || Number(form.amount) <= 0) errs.amount = "Enter an amount.";
    if (form.status === "received" && !form.receivedDate) errs.receivedDate = "Date received is required.";
    setErrors(errs);
    if (Object.keys(errs).length) return;
    setSaving(true);
    try {
      const data = {
        donorName: form.donorName.trim(), donorEmail: form.donorEmail.trim(), amount: Number(form.amount),
        currency: form.currency, method: form.method, status: form.status,
        receivedDate: form.status === "received" ? form.receivedDate : null,
        eventId: form.eventId || null, notes: form.notes, linkedLeadId: form.linkedLeadId || null,
      };
      const id = await saveDonation(donation.id || null, data);
      // Bump the event total only on the transition into "received".
      if (form.status === "received" && form.eventId && !wasReceived) {
        await markDonationReceived(id, { receivedDate: form.receivedDate, amount: Number(form.amount), eventId: form.eventId });
      }
      showToast({ intent: "success", title: donation.id ? "Donation updated" : "Donation logged" });
      onClose();
    } catch (err) {
      showToast({ intent: "danger", title: "Couldn't save", body: err.message });
      setSaving(false);
    }
  };

  return (
    <Modal open onClose={onClose} size="lg" title={donation.id ? "Edit donation" : "Log a donation"}
      footer={<><Button variant="ghost" onClick={onClose}>Cancel</Button><Button variant="primary" onClick={submit} loading={saving}>Save donation</Button></>}>
      {donation._prefillFromLead && (
        <div style={{ background: "var(--accent-yellow-soft)", color: "var(--warning)", padding: 12, borderRadius: "var(--radius-md)", marginBottom: 20, fontSize: 13, display: "flex", gap: 8, alignItems: "center" }}>
          <Icon.Info size={16} />Prefilled from lead: <strong>{donation._prefillFromLead}</strong>
        </div>
      )}
      <div className="stack-lg">
        <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 16 }}>
          <Field label="Donor name" required error={errors.donorName}><Input value={form.donorName} onChange={update("donorName")} error={!!errors.donorName} /></Field>
          <Field label="Donor email (optional)"><Input type="email" value={form.donorEmail} onChange={update("donorEmail")} /></Field>
        </div>
        <div style={{ display: "grid", gridTemplateColumns: "2fr 1fr", gap: 16 }}>
          <Field label="Amount" required error={errors.amount}><Input type="number" value={form.amount} onChange={update("amount")} error={!!errors.amount} /></Field>
          <Field label="Currency"><Select value={form.currency} onChange={update("currency")}><option>GHS</option><option>USD</option><option>GBP</option><option>EUR</option></Select></Field>
        </div>
        <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 16 }}>
          <Field label="Method"><Select value={form.method} onChange={update("method")}><option value="bank">Bank transfer</option><option value="momo">Mobile money</option><option value="cash">Cash</option><option value="other">Other</option></Select></Field>
          <Field label="For event (optional)"><Select value={form.eventId} onChange={update("eventId")}><option value="">General fund</option>{events.map((e) => <option key={e.slug} value={e.slug}>{e.title}</option>)}</Select></Field>
        </div>
        <fieldset style={{ border: 0, padding: 0, margin: 0 }}>
          <legend className="field__label" style={{ marginBottom: 8 }}>Status</legend>
          <div className="radio-group radio-group--inline">
            <label className="radio"><input type="radio" name="ds" value="pledged" checked={form.status === "pledged"} onChange={update("status")} />Pledged (not yet received)</label>
            <label className="radio"><input type="radio" name="ds" value="received" checked={form.status === "received"} onChange={update("status")} />Received</label>
          </div>
        </fieldset>
        {form.status === "received" && (
          <Field label="Date received" required error={errors.receivedDate}><Input type="date" value={form.receivedDate || ""} onChange={update("receivedDate")} error={!!errors.receivedDate} /></Field>
        )}
        <Field label="Notes (optional)" helper="For your internal records. Not visible to the donor."><Textarea value={form.notes} onChange={update("notes")} rows={3} /></Field>
      </div>
    </Modal>
  );
}
